import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

/**
 * Drop-in replacement for React.lazy that survives deploys.
 *
 * Code-split chunks are content-hashed (e.g. Login-DRSgxniU.js). When a new
 * build ships while a user has an old tab open, the old chunk names no longer
 * exist on the server, so a navigation that lazy-loads one gets the SPA
 * index.html fallback (MIME text/html) and the dynamic import throws
 * "Failed to fetch dynamically imported module" — crashing into the
 * ErrorBoundary.
 *
 * On that failure we reload the page once to pull the fresh bundle, instead of
 * showing an error. A sessionStorage timestamp guards against an infinite
 * reload loop if the import keeps failing for some other reason (e.g. offline).
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const KEY = 'chunk-reload-at';
      const last = Number(sessionStorage.getItem(KEY) || '0');
      // Reload at most once per 10s window — enough to recover from a deploy,
      // not enough to loop on a persistent failure.
      if (typeof window !== 'undefined' && Date.now() - last > 10_000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        // Never resolve: keep the Suspense fallback up until the reload lands.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
